<template>
  <ClientOnly>
    <div
      :class="{
        'flex flex-col items-center justify-center gap-8 pt-9 text-center md:flex-row': true,
        hidden: total <= pageLimit,
      }"
    >
      <div>
        <PTCLink
          v-if="pageNumber > 1"
          :class="{
            'text-18px': true,
          }"
          :to="`?page=${pageNumber - 1}`"
          @click.prevent="emitChange({ pageNumber: pageNumber - 1 })"
        >
          &lt;
        </PTCLink>
        <span
          v-for="pageButton in pageButtons"
          :key="pageButton.pageNumber"
          :class="{
            'mx-3 text-sm text-www-color-black': true,
            'mx-3 inline-block h-6 w-6 rounded-50% bg-www-color-primary-lighter text-www-color-white':
              pageNumber === pageButton.pageNumber,
          }"
        >
          <PTCLink
            v-if="pageButton.isDots"
            :class="{
              'text-18px': true,
            }"
            :to="`?page=${pageNumber - 3}`"
            @click.prevent="emitChange({ pageNumber: pageNumber - 3 })"
          >
            ...
          </PTCLink>
          <PTCLink
            v-else
            :class="{
              'text-18px': true,
            }"
            :to="`?page=${pageButton.pageNumber}`"
            @click.prevent="emitChange({ pageNumber: pageButton.pageNumber })"
          >
            {{ pageButton.pageNumber }}
          </PTCLink>
        </span>

        <PTCLink
          v-if="pageNumber < totalNumberOfPages - 1"
          :class="{
            'text-18px': true,
          }"
          :to="`?page=${pageNumber + 1}`"
          @click.prevent="emitChange({ pageNumber: pageNumber + 1 })"
        >
          &gt;
        </PTCLink>
      </div>
      <div>
        <label
          :class="{
            'p-2 text-sm': true,
          }"
        >
          {{ goToPage }}
          <input
            v-model="chosenPage"
            :class="{
              'ml-2 h-8 w-20 border border-www-color-neutral-light px-10px': true,
            }"
            type="text"
            @keyup.enter="onChosePage"
          />
        </label>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import PTCLink from '~/components/Atmos/PTCLink/PTCLink.vue';

const props = defineProps({
  total: {
    type: Number,
    required: true,
  },
  pageNumber: {
    type: Number,
    required: true,
  },
  pageLimit: {
    type: Number,
    required: true,
  },
  goToPage: {
    type: String,
    required: true,
  },
});
const { total, pageNumber, pageLimit } = toRefs(props);
type PageButton = {
  pageNumber: number;
  isDots: boolean;
};

const totalNumberOfPages = computed(() => {
  if (total.value === 0) {
    return 0;
  }
  return Math.ceil(total.value / pageLimit.value);
});
const chosenPage = ref('');

const pageButtons: Ref<Array<PageButton>> = computed(() => {
  if (totalNumberOfPages.value <= 7) {
    return Array.from({ length: totalNumberOfPages.value }, (_, i) => ({
      pageNumber: i + 1,
      isDots: false,
    }));
  }

  if (pageNumber.value < 4) {
    return [
      ...Array.from({ length: 6 }, (_, i) => ({
        pageNumber: i + 1,
        isDots: false,
      })),
      { pageNumber: 7, isDots: true },
      { pageNumber: totalNumberOfPages.value, isDots: false },
    ];
  }
  if (pageNumber.value < totalNumberOfPages.value - 5) {
    return [
      { pageNumber: 1, isDots: false },
      { pageNumber: 2, isDots: true },

      ...Array.from({ length: 3 }, (_, i) => ({
        pageNumber: pageNumber.value - 1 + i,
        isDots: false,
      })),

      { pageNumber: totalNumberOfPages.value - 1, isDots: true },
      { pageNumber: totalNumberOfPages.value, isDots: false },
    ];
  }

  return [
    { pageNumber: 1, isDots: false },
    { pageNumber: 2, isDots: true },
    ...Array.from({ length: 5 }, (_, i) => ({
      pageNumber: totalNumberOfPages.value - 5 + i + 1,
      isDots: false,
    })),
  ];
});

const onChosePage = () => {
  nextTick(() => {
    try {
      const page = parseInt(`${chosenPage.value}`, 10);
      chosenPage.value = '';
      if (page > totalNumberOfPages.value) {
        emitChange({ pageNumber: totalNumberOfPages.value });
      } else if (page > 0) {
        emitChange({ pageNumber: page });
      } else {
        emitChange({ pageNumber: 1 });
      }
    } catch (e) {
      console.error(e);
      return;
    }
  });
};

const emit = defineEmits<{
  (_evt: 'pageChange', _payload: PageChangedPayload): void;
}>();

const emitChange = (payload: PageChangedPayload) => {
  emit('pageChange', payload);
};
</script>
